
// A date the client TYPES, as MM / DD / YYYY boxes with the number pad, in
// place of <input type="date">. On an iPhone or iPad the native date input is
// a calendar with no keyboard, so a client born in 1954 had to page back
// seventy-odd years to reach their birth year (client feedback, Oct 2026).
// The /schedule wizard already asks this way (DobInput.vue); this speaks the
// ISO 'YYYY-MM-DD' string the native input did, so it drops into v-model.
//
// The model only ever holds a real date, or null once every box is empty. A
// half-typed or impossible date is NOT emitted: the survey pages autosave each
// field they watch, and writing null mid-edit would PATCH over the date of
// birth on file, the same one /verify checks. Instead `incomplete` reports it,
// so the page can refuse to move on until it is finished.
import DobInput from '~/components/schedule/v2/parts/DobInput.vue'

function pad (n) { return String(n).padStart(2, '0') }

// Date.UTC rolls 02/30 over to 03/02; a real date survives the round trip.
function isRealDate (y, m, d) {
    const dt = new Date(Date.UTC(y, m - 1, d))
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

export default {
    components: { DobInput },
    props: {
        value: { type: String, default: null },
        min: { type: String, default: '1900-01-01' },
        max: { type: String, default: null },
        id: { type: String, default: null },
        inputClass: { type: String, default: '' },
        // Screen-reader name for the boxes ("Birth month", "Part A month").
        label: { type: String, default: 'Birth' },
        // Offer the browser's saved birthday. Off for dates that are not one.
        birthday: { type: Boolean, default: true },
    },
    data () {
        return {
            mm: '',
            dd: '',
            yyyy: '',
            pending: false,
            showError: false,
            // What we last emitted, so the parent echoing it back through
            // `value` does not re-fill (and re-pad) the boxes mid-typing.
            emitted: undefined,
        }
    },
    watch: {
        value (v) {
            if (v !== this.emitted) { this.split(v) }
        },
    },
    created () {
        this.split(this.value)
    },
    methods: {
        split (v) {
            const [y, m, d] = (v || '').split('-')
            this.yyyy = y || ''
            this.mm = m || ''
            this.dd = d || ''
            this.emitted = undefined
            this.showError = false
            this.setPending(false)
        },
        set (field, v) {
            this[field] = v
            this.sync()
        },
        sync () {
            const { mm, dd, yyyy } = this
            if (!mm && !dd && !yyyy) {
                this.showError = false
                this.setPending(false)
                return this.emit(null)
            }
            if (!(mm && dd && yyyy.length === 4)) {
                this.showError = false
                return this.setPending(true)
            }
            const iso = this.toIso()
            this.showError = !iso
            this.setPending(!iso)
            if (iso) { this.emit(iso) }
        },
        toIso () {
            const y = parseInt(this.yyyy, 10)
            const m = parseInt(this.mm, 10)
            const d = parseInt(this.dd, 10)
            if (!isRealDate(y, m, d)) { return null }
            const iso = `${y}-${pad(m)}-${pad(d)}`
            if (this.min && iso < this.min) { return null }
            if (this.max && iso > this.max) { return null }
            return iso
        },
        emit (v) {
            this.emitted = v
            if (v !== this.value) { this.$emit('input', v) }
        },
        setPending (v) {
            if (v !== this.pending) {
                this.pending = v
                this.$emit('incomplete', v)
            }
        },
    },
}
